import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from '@themesberg/react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL, END_POINT_QUOTES } from '../const';
import SearchableSelect from './SearchableSelect';

const readToken = fallback => {
  const raw = sessionStorage.getItem('quotetoken');
  if (!raw) return fallback || '';
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string' && parsed) return parsed;
  } catch (_error) { /* token is stored as a raw string */ }
  return raw.replace(/^"|"$/g, '') || fallback || '';
};

const authHeaders = token => ({ Accept: 'application/json', Authorization: `Bearer ${token}` });

const QuoteTransferModal = ({
  show,
  onHide,
  token,
  users = [],
  sourceUser = null,
  selectedQuotes,
  onTransferred,
}) => {
  const presetQuotes = selectedQuotes || [];
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [toUserId, setToUserId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const lockedQuotes = presetQuotes.length > 0;
  const sourceId = sourceUser?.id || presetQuotes[0]?.created_by_id;
  const sourceName = sourceUser?.name || presetQuotes[0]?.sales_executive?.name || 'the selected user';
  const destinationUsers = useMemo(
    () => users.filter(user => String(user.id) !== String(sourceId)),
    [users, sourceId]
  );
  const destination = destinationUsers.find(user => String(user.id) === String(toUserId));

  useEffect(() => {
    if (!show) {
      setQuotes([]);
      setSelectedIds([]);
      setToUserId('');
      setReason('');
      setSubmitting(false);
      setError('');
      setLoadingQuotes(false);
      return undefined;
    }
    if (lockedQuotes) {
      setQuotes(presetQuotes);
      setSelectedIds(presetQuotes.map(quote => quote.id));
      setLoadingQuotes(false);
      setError('');
      return undefined;
    }
    if (!sourceUser?.id) {
      setLoadingQuotes(false);
      return undefined;
    }
    const sessionToken = readToken(token);
    if (!sessionToken) {
      setError('Your session expired. Please sign in again.');
      setLoadingQuotes(false);
      return undefined;
    }
    let active = true;
    setLoadingQuotes(true);
    setError('');
    axios.get(API_URL + END_POINT_QUOTES + '/transferable', {
      params: { from_user_id: sourceUser.id },
      headers: authHeaders(sessionToken),
    }).then(response => {
      if (!active) return;
      const rows = response.data.data?.quotes || [];
      setQuotes(rows);
      setSelectedIds(rows.map(quote => quote.id));
    }).catch(failure => {
      if (active) setError(failure.response?.data?.message || 'Unable to load quotes for this user.');
    }).finally(() => {
      if (active) setLoadingQuotes(false);
    });
    return () => { active = false; };
  }, [show, lockedQuotes, sourceUser?.id, token]);

  const toggleQuote = id => {
    if (lockedQuotes || submitting) return;
    setSelectedIds(current => current.includes(id)
      ? current.filter(value => value !== id)
      : [...current, id]);
  };
  const allSelected = quotes.length > 0 && selectedIds.length === quotes.length;

  const confirmAndTransfer = async () => {
    const sessionToken = readToken(token);
    if (submitting || !destination || selectedIds.length === 0 || String(sourceId) === String(destination.id)) return;
    if (!sessionToken) {
      setError('Your session expired. Please sign in again.');
      return;
    }
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Transfer quote ownership?',
      text: `You are about to transfer ${selectedIds.length} quote${selectedIds.length === 1 ? '' : 's'} from ${sourceName} to ${destination.name}. This action will change the quote owner. Do you want to continue?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, transfer',
      cancelButtonText: 'Cancel',
    });
    if (!confirmation.isConfirmed) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(API_URL + END_POINT_QUOTES + '/transfer', {
        quote_ids: selectedIds.map(Number),
        to_user_id: Number(destination.id),
        from_user_id: Number(sourceId),
        reason: reason.trim() || undefined,
      }, { headers: authHeaders(sessionToken) });
      await Swal.fire({ icon: 'success', title: 'Transferred', text: response.data.message });
      onTransferred?.(response.data.data);
      onHide();
    } catch (failure) {
      setError(failure.response?.data?.message || 'Failed to transfer quotes.');
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} size="lg" centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Transfer Quotes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div role="alert" className="alert alert-danger">{error}</div>}
        <p className="text-muted">
          Source: <strong>{sourceName}</strong>
          {destination ? <> → Destination: <strong>{destination.name}</strong></> : null}
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Destination user</Form.Label>
          <SearchableSelect
            inputId="to_user_id"
            name="to_user_id"
            options={destinationUsers.map(user => ({ value: String(user.id), label: user.name }))}
            value={toUserId}
            onChange={setToUserId}
            placeholder="Select destination user"
            isDisabled={submitting}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Reason (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={event => setReason(event.target.value)}
            disabled={submitting}
            placeholder="Sales account reassignment"
          />
        </Form.Group>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{selectedIds.length} quote{selectedIds.length === 1 ? '' : 's'} selected</strong>
          {!lockedQuotes && quotes.length > 0 && (
            <Button type="button" size="sm" variant="outline-secondary" disabled={submitting || loadingQuotes}
              onClick={() => setSelectedIds(allSelected ? [] : quotes.map(quote => quote.id))}>
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          )}
        </div>
        {loadingQuotes ? <div role="status">Loading quotes...</div> : (
          <div className="quote-transfer-list">
            {quotes.length === 0 ? <div className="text-muted">No quotes found for this user.</div>
              : quotes.map(quote => (
                <label key={quote.id} className="quote-transfer-item">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(quote.id)}
                    disabled={lockedQuotes || submitting}
                    onChange={() => toggleQuote(quote.id)}
                  />
                  <span>{quote.offer_no || ('#' + quote.id)}</span>
                  <span className="text-muted">{quote.status}</span>
                  <span>₹ {Number(quote.total || 0).toLocaleString('en-IN')}</span>
                </label>
              ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button variant="primary" onClick={confirmAndTransfer}
          disabled={submitting || loadingQuotes || !destination || selectedIds.length === 0 || String(sourceId) === String(toUserId)}>
          {submitting ? 'Transferring...' : 'Transfer quotes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuoteTransferModal;
